/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import * as THREE from 'three';
import type { ColorOptions, IDrawer } from './Interfaces';
import _ from 'underscore';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { Line2 } from 'three/examples/jsm/lines/Line2';
import earcut from 'earcut';

export class Drawer implements IDrawer {
  public constructor() {
    this.createPointTexture = _.memoize(
      this.createPointTexture.bind(this),
      (color: number | string, size: number) => `${color}_${size}`
    );
  }

  public createPointTexture(color: number | string, size: number): THREE.Texture {
    if (typeof color === 'number') {
      color = '#' + color.toString(16);
    }

    const matCanvas = document.createElement('canvas');
    matCanvas.width = matCanvas.height = size;
    const matContext = matCanvas.getContext('2d');
    if (!matContext) throw new Error('Failed to obtain 2d context');

    // create texture object from canvas.
    const texture = new THREE.Texture(matCanvas);

    // Draw a circle
    const center = size / 2;
    matContext.beginPath();
    matContext.arc(center, center, size / 2, 0, 2 * Math.PI, false);
    matContext.closePath();
    matContext.fillStyle = color;
    matContext.fill();

    // need to set needsUpdate
    texture.needsUpdate = true;

    // return a texture made from the canvas
    return texture;
  }

  public createPoint(
    position: THREE.Vector3,
    { color }: ColorOptions,
    pointSize: number,
    pointOpacity?: number
  ): THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
    const pointGeometry = new THREE.BufferGeometry().setFromPoints([position]);

    const pointMaterial = new THREE.PointsMaterial({
      size: pointSize,
      map: this.createPointTexture(color, pointSize),
      transparent: true,
      depthWrite: false,
      depthTest: false,
      opacity: pointOpacity ?? 1
    });

    return new THREE.Points(pointGeometry, pointMaterial);
  }

  public updatePointColor(point: THREE.Points, { color }: ColorOptions, pointSize: number): void {
    const pointOpacity = (point.material as THREE.Material).opacity;

    point.material = new THREE.PointsMaterial({
      size: pointSize,
      map: this.createPointTexture(color, pointSize),
      transparent: true,
      depthWrite: false,
      depthTest: false,
      opacity: pointOpacity
    });
  }

  public updatePointPosition(point: THREE.Points, position: THREE.Vector3): void {
    point.geometry = new THREE.BufferGeometry().setFromPoints([position]);
  }

  public updateLineColor(line: THREE.Line, { color }: ColorOptions): void {
    line.material = new THREE.LineBasicMaterial({
      color,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 1
    });
  }

  public createLine(
    points: Array<THREE.Vector3>,
    { color }: ColorOptions,
    lineOpacity?: number
  ): THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> {
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: lineOpacity ?? 1
    });

    return new THREE.Line(geometry, material);
  }

  public createDashedLine(
    points: Array<THREE.Vector3>,
    { color }: ColorOptions,
    pointOpacity?: number
  ): THREE.Line<THREE.BufferGeometry, THREE.LineDashedMaterial> {
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineDashedMaterial({
      color,
      linewidth: 1,
      scale: 1,
      dashSize: 0.5,
      gapSize: 0.5,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: pointOpacity ?? 1
    });
    const line = new THREE.Line(geometry, material);
    line.computeLineDistances();

    return line;
  }

  public createThickLine(points: Array<THREE.Vector3>, color: number, vWidth: number, vHeight: number): Line2 {
    const positions: Array<number> = [];
    const colors: Array<number> = [];

    for (const point of points) {
      positions.push(point.x, point.y, point.z);
      colors.push(0, 128, 255);
    }

    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    geometry.setColors(colors);

    const material = new LineMaterial({
      color,
      linewidth: 3,
      vertexColors: false,
      alphaToCoverage: true,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide
    });

    const line = new Line2(geometry, material);
    line.computeLineDistances();
    line.scale.set(1, 1, 1);
    material.resolution.set(vWidth, vHeight);
    return line;
  }

  public updateLine(line: THREE.Line, points: Array<THREE.Vector3>): void {
    line.geometry = new THREE.BufferGeometry().setFromPoints(points);
  }

  public createCircle(
    center: THREE.Vector3,
    normal: THREE.Vector3,
    radius: number,
    { color }: ColorOptions
  ): THREE.LineLoop {
    const geometry = new THREE.RingGeometry(radius, radius, 64, 64);

    geometry.lookAt(normal);

    const material = new THREE.LineBasicMaterial({ color, depthTest: false });

    const line = new THREE.LineLoop(geometry, material);
    line.position.copy(center);

    return line;
  }

  public createCircleFill(
    center: THREE.Vector3,
    normal: THREE.Vector3,
    radius: number,
    { color }: ColorOptions
  ): THREE.Object3D {
    const fillGeometry = new THREE.CircleGeometry(radius, 64);
    fillGeometry.lookAt(normal);
    const fillMaterial = new THREE.MeshPhongMaterial({
      color: color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.15,
      depthWrite: false,
      depthTest: false
    });

    const fill = new THREE.Mesh(fillGeometry, fillMaterial);
    fill.position.copy(center);

    return fill;
  }

  public createFiller(points: Array<THREE.Vector3>, opacity = 0.15): THREE.Mesh {
    // Flatten the points array into a 2D array for Earcut (just x and y).
    const flatCoordinates: Array<number> = points.flatMap((point) => [point.x, point.y]);

    // Perform triangulation using Earcut, which now only expects 2D points.
    const triangles: Array<number> = earcut(flatCoordinates);

    // You can now use these indices directly with your original 3D points.
    const geometry = new THREE.BufferGeometry();

    // Still, provide the full 3D coordinates to Three.js.
    const vertices = new Float32Array(points.map((point) => [point.x, point.y, point.z]).flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

    // Use the triangulation result as the index for the geometry.
    const indices = triangles.length > 65535 ? new Uint32Array(triangles) : new Uint16Array(triangles);
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    geometry.computeVertexNormals();

    const material = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      color: '#ffffff',
      opacity: opacity,
      transparent: true,
      depthTest: false
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.matrixAutoUpdate = false;
    mesh.renderOrder = 1;

    return mesh;
  }
}
